import type { Difficulty, GenContext, Question, QuestionFamily, SubjectId } from '@/core/types';
import type { DomainDef, SkillDef, ContentDef } from '../../pack';
import { bankFamily, crit, type AuthoredItem, type ConceptItem } from '@/engine/questions/bank';
import { choice, forSkill, matching, text, tokens } from '@/engine/questions/build';
import { B, bi, loc, L as tr, type Bi, type DeepBi } from '@/i18n';

/**
 * Language packs share one structure (vocabulary, grammar, communication,
 * translation) but every word, form and sentence is language-specific data.
 * Meanings carry an optional Norwegian gloss (`nb`) so a Norwegian-speaking
 * learner sees "Bonjour = hei / god dag" instead of an English detour.
 */

export interface LexEntry {
  w: string;
  en: string;
  /** Norwegian meaning (falls back to English). */
  nb?: string;
  cat: string;
  /** Words with the same sense are never distractors for each other. */
  sense?: string;
  gender?: string;
}

export interface FormTable {
  verb: string;
  meaning: string;
  meaningNb?: string;
  irregular: boolean;
  /** key (pronoun / aspect) → form */
  forms: Record<string, string>;
}

export interface SentenceItem {
  target: string;
  en: string;
  nb?: string;
  /** Other word orders that are equally correct (same tokens). */
  alt?: string[];
}

export interface LanguageSpec {
  subjectId: SubjectId;
  prefix: string;
  name: string;
  /** Norwegian name in running text, e.g. "fransk". */
  nameNb: string;
  speechLang: string;
  lexicon: LexEntry[];
  /** Keys of the form table in display order (pronouns or aspects). */
  formKeys: string[];
  formKeyLabel: string;
  /** Norwegian display names for keys that are English words (e.g. Tagalog aspects). */
  formKeyNb?: Record<string, string>;
  /** How a key + form is displayed in a sentence (handles elision etc.). */
  phrase: (key: string, form: string) => string;
  tables: FormTable[];
  genders?: { categories: string[]; items: [string, string][]; label: string };
  sentences: SentenceItem[];
  errorItems: DeepBi<AuthoredItem>[];
  situations: { prompt: Bi; answer: string; wrong: string[] }[];
  reading: { title: string; text: string; items: DeepBi<AuthoredItem>[] };
  minimalPairs: { a: string; b: string; aEn: string; bEn: string; aNb?: string; bNb?: string }[];
  intro: { keywords: { name: string[]; age: string[]; place: string[] }; exemplar: string };
  grammarNotes: { forms: Bi; gender?: Bi; order: Bi };
  /** Optional extra grammar skill (e.g. Tagalog markers). */
  extraGrammar?: { id: string; name: string; desc: string; items: DeepBi<AuthoredItem>[]; content: ContentDef; stage: SkillDef['stage']; track: string };
}

/** CEFR-like levels shared by every language subject. */
export const LANGUAGE_LEVELS = [
  loc({ id: 'A1', label: B('Beginner (A1-like)', 'Nybegynner (omtrent A1)'), order: 1, description: B('First words and phrases.', 'De første ordene og uttrykkene.') }),
  loc({ id: 'A2', label: B('Elementary (A2-like)', 'Grunnleggende (omtrent A2)'), order: 2, description: B('Everyday topics and simple sentences.', 'Hverdagstemaer og enkle setninger.') }),
  loc({ id: 'B1', label: B('Intermediate (B1-like)', 'Middels (omtrent B1)'), order: 3, description: B('Connected sentences, short texts.', 'Sammenhengende setninger og korte tekster.') }),
];

const LEVEL_STAGE: Record<string, SkillDef['stage']> = { A1: 'G8', A2: 'G9', B1: 'G10' };

const CAT_NB: Record<string, string> = {
  Greeting: 'Hilsen', Farewell: 'Avskjed', Politeness: 'Høflighet', Number: 'Tall', Family: 'Familie',
  Food: 'Mat', Day: 'Dag', School: 'Skole', Colour: 'Farge',
};
const KEY_LABEL_NB: Record<string, string> = { Pronoun: 'Pronomen', Aspect: 'Aspekt' };

const enOf = (v: Bi) => (typeof v === 'string' ? v : v.en);
const nbOf = (v: Bi) => (typeof v === 'string' ? v : v.nb);
const pair = (en: string, nb: string | undefined): Bi => (nb && nb !== en ? B(en, nb) : en);
const catB = (c: string) => pair(c, CAT_NB[c]);
const genderNb = (g: string) => g.replace(/masculine/g, 'hankjønn').replace(/feminine/g, 'hunkjønn').replace(/neuter/g, 'intetkjønn');
const genderB = (g: string) => pair(g, genderNb(g));

/** Meaning of a word in the active language. */
const mean = (e: LexEntry) => tr(e.en, e.nb ?? e.en);
const sentenceMeaning = (s: SentenceItem) => tr(s.en, s.nb ?? s.en);
/** End a sentence with a full stop unless it already has end punctuation. */
const stop = (s: string) => (/[.?!]$/.test(s) ? s : `${s}.`);

export function buildLanguage(spec: LanguageSpec): { domains: DomainDef[]; families: QuestionFamily[] } {
  const p = spec.prefix;
  const N = spec.nameNb;
  const families: QuestionFamily[] = [];
  const lexBy = (cats: string[]) => spec.lexicon.filter((e) => cats.includes(e.cat));

  type VocabRow = [id: string, name: Bi, desc: Bi, cats: string[], level: string, why: Bi];
  const vocabSkills: VocabRow[] = [
    [`${p}.greetings`, B('Greetings & politeness', 'Hilsener og høflighet'), B('Greet, take leave, thank and apologise appropriately.', 'Hilse, ta farvel, takke og be om unnskyldning på en passende måte.'), ['Greeting', 'Farewell', 'Politeness'], 'A1', B('Choosing the right phrase depends on time of day and situation.', 'Hvilket uttrykk som passer, avhenger av tid på døgnet og situasjonen.')],
    [`${p}.numbers`, B('Numbers', 'Tall'), B('Recognise and use numbers.', 'Kjenne igjen og bruke tall.'), ['Number'], 'A1', B('Numbers are used for age, prices and time.', 'Tall brukes om alder, priser og klokkeslett.')],
    [`${p}.family`, B('Family & people', 'Familie og personer'), B('Talk about family members and people.', 'Snakke om familiemedlemmer og andre personer.'), ['Family'], 'A1', B('Family vocabulary is the basis of introducing yourself.', 'Ord for familie er grunnlaget når du skal presentere deg selv.')],
    [`${p}.food`, B('Food & drink', 'Mat og drikke'), B('Name common food and drink.', 'Kunne navn på vanlig mat og drikke.'), ['Food'], 'A2', B('Useful for shopping, cafés and travel.', 'Nyttig når du handler, går på kafé og reiser.')],
    [`${p}.days`, B('Days & school', 'Dager og skole'), B('Days of the week and school words.', 'Ukedager og ord fra skolen.'), ['Day', 'School'], 'A2', B('Plans and timetables use days and school words.', 'Planer og timeplaner bruker ukedager og skoleord.')],
    [`${p}.colours`, B('Colours & description', 'Farger og beskrivelser'), B('Describe things with colours.', 'Beskrive ting med farger.'), ['Colour'], 'A1', B('Descriptions make your language vivid.', 'Beskrivelser gjør språket ditt levende.')],
  ];

  const vocabDefs: SkillDef[] = vocabSkills.map(([id, name, desc, cats, level, why]) => {
    const entries = lexBy(cats);
    const concepts: DeepBi<ConceptItem>[] = entries.map((e) => ({ term: e.w, meaning: pair(e.en, e.nb), category: catB(e.cat), sense: e.sense }));
    const items: DeepBi<AuthoredItem>[] = [];
    if (id.endsWith('.greetings')) {
      spec.situations.forEach((s, i) => items.push({ id: `sit${i}`, d: (3 + (i % 3)) as Difficulty, kind: 'scenario', prompt: s.prompt, correct: s.answer, wrong: s.wrong, solution: [B(`"${s.answer}" fits this situation.`, `«${s.answer}» passer i denne situasjonen.`)] }));
    }
    families.push(bankFamily({
      skillId: id, subjectId: spec.subjectId, objective: enOf(name), concepts, categories: cats.length > 1 ? cats.map(catB) : undefined, items,
      termLabel: B(`${spec.name} word`, `ord på ${N}`), meaningLabel: B('meaning', 'betydning'), speechLang: spec.speechLang,
    }));
    families.push(produceFamily(id, spec, entries));
    families.push(listenFamily(id, spec, entries));
    return {
      id, name, desc, stage: LEVEL_STAGE[level], track: level, range: [1, 6], target: 4,
      aliases: [`${spec.name.toLowerCase()} ${enOf(name).toLowerCase()}`, `${N} ${nbOf(name).toLowerCase()}`],
      c: {
        intro: B(`${enOf(name)} in ${spec.name}. ${enOf(why)}`, `${nbOf(name)} på ${N}. ${nbOf(why)}`),
        explain: [
          B('Learn words in small groups and say them aloud. Studio checks recognition (what does it mean?), production (how do you say it?) and listening.',
            'Lær ordene i små grupper og si dem høyt. Studio sjekker gjenkjenning (hva betyr det?), produksjon (hvordan sier du det?) og lytting.'),
          B(`Example words: ${entries.slice(0, 5).map((e) => `${e.w} — ${e.en}`).join('; ')}.`, `Eksempelord: ${entries.slice(0, 5).map((e) => `${e.w} – ${e.nb ?? e.en}`).join('; ')}.`),
        ],
        defs: entries.slice(0, 6).map((e) => [e.w, pair(e.en, e.nb)] as [Bi, Bi]),
        intuition: [B('Link each word to a picture or situation, not only to a Norwegian/English translation.', 'Knytt hvert ord til et bilde eller en situasjon, ikke bare til en norsk eller engelsk oversettelse.'), 'none'],
        mis: id.endsWith('.greetings')
          ? [[`${p}.mis.thanks-greeting`, B('Using a "thank you" phrase as a greeting', 'Å bruke et «takk»-uttrykk som hilsen'), B('Politeness phrases and greetings both appear at the start of conversations.', 'Høflighetsuttrykk og hilsener dukker begge opp i starten av en samtale.'), B('Sort phrases by function: greeting, farewell, thanks.', 'Sorter uttrykkene etter funksjon: hilsen, avskjed, takk.')]]
          : [[`${p}.mis.false-friend`, B('Guessing meaning from a similar-looking Norwegian word', 'Å gjette betydningen ut fra et norsk ord som ligner'), B('Some words look alike but mean something else.', 'Noen ord ser like ut, men betyr noe annet.'), B('Check the meaning in context.', 'Sjekk betydningen i sammenhengen.')]],
        apply: B(`Use these words in a short dialogue in ${spec.name}.`, `Bruk disse ordene i en kort dialog på ${N}.`),
        keys: [[B('learn by function and situation', 'lær etter funksjon og situasjon'), ['situation', 'function', 'context', 'situasjon', 'funksjon', 'sammenheng']]],
      },
    };
  });

  /* ---------------- grammar ---------------- */
  const formsId = `${p}.forms`;
  const aspect = spec.formKeyLabel === 'Aspect';
  const keyLabel = pair(spec.formKeyLabel, KEY_LABEL_NB[spec.formKeyLabel]);
  families.push(formsFamily(formsId, spec));
  families.push(bankFamily({ skillId: formsId, subjectId: spec.subjectId, objective: 'Spot form errors', items: spec.errorItems, speechLang: spec.speechLang, range: [3, 6] }));

  const grammarDefs: SkillDef[] = [
    {
      id: formsId, name: aspect ? B('Verb aspects', 'Verbaspekt') : B('Verb forms (present tense)', 'Verbformer (presens)'), stage: 'G9', track: 'A2', range: [1, 7], target: 5,
      desc: spec.grammarNotes.forms, pre: [`${p}.family`],
      aliases: [`${spec.name.toLowerCase()} verbs`, 'conjugation', 'bøying av verb', `${N} verb`],
      c: {
        intro: B(`Verbs in ${spec.name} change form. ${enOf(spec.grammarNotes.forms)}`, `Verb på ${N} skifter form. ${nbOf(spec.grammarNotes.forms)}`),
        explain: [
          B(`Key verbs: ${spec.tables.map((t) => `${t.verb} (${t.meaning})`).join(', ')}.`, `Viktige verb: ${spec.tables.map((t) => `${t.verb} (${t.meaningNb ?? t.meaning})`).join(', ')}.`),
          ...spec.tables.slice(0, 2).map((t) => `${t.verb}: ${spec.formKeys.map((k) => spec.phrase(k, t.forms[k])).join(', ')}.`),
        ],
        defs: [[keyLabel, aspect ? B('Whether an action is completed, ongoing or not yet begun.', 'Om en handling er fullført, pågår eller ikke har begynt ennå.') : B('The person doing the action (I, you, he/she …).', 'Personen som utfører handlingen (jeg, du, han/hun …).')]],
        intuition: [B('Learn the pattern once, then reuse it for every regular verb.', 'Lær mønsteret én gang, og bruk det så på alle regelrette verb.'), 'none'],
        mis: [[`${p}.mis.form`, B('Using the same verb form for every person', 'Å bruke samme verbform for alle personer'), B('Norwegian verbs barely change with the person, so it is tempting to transfer that habit.', 'Norske verb endrer seg nesten ikke etter person, så det er fristende å overføre den vanen.'), B('Match the ending to the subject.', 'Tilpass endelsen til subjektet.')]],
        apply: B('Describe what you and others do every day.', 'Beskriv hva du og andre gjør hver dag.'),
        keys: [[B('the form matches the subject', 'formen passer til subjektet'), ['subject', 'person', 'pronoun', 'aspect', 'subjekt', 'pronomen', 'aspekt']]],
      },
    },
  ];

  if (spec.genders) {
    const g = spec.genders;
    const gid = `${p}.gender`;
    families.push(bankFamily({
      skillId: gid, subjectId: spec.subjectId, objective: g.label,
      concepts: g.items.map(([w, c]) => ({ term: w, meaning: genderB(c), category: genderB(c), sense: c })),
      categories: g.categories.map(genderB), termLabel: B('noun', 'substantiv'), meaningLabel: B('gender', 'kjønn'), speechLang: spec.speechLang, range: [2, 6],
    }));
    grammarDefs.push({
      id: gid, name: g.label, stage: 'G9', track: 'A2', range: [2, 6], target: 4, pre: [`${p}.family`, `${p}.food`],
      desc: spec.grammarNotes.gender ?? B('Noun gender and articles.', 'Substantivets kjønn og artikler.'),
      aliases: ['gender', 'articles', 'kjønn'],
      c: {
        intro: spec.grammarNotes.gender ?? B('Nouns have grammatical gender.', 'Substantiv har grammatisk kjønn.'),
        explain: [
          B('Learn every noun together with its gender marker.', 'Lær hvert substantiv sammen med kjønnsmarkøren.'),
          B(`Examples: ${g.items.slice(0, 6).map(([w, c]) => `${w} (${c})`).join(', ')}.`, `Eksempler: ${g.items.slice(0, 6).map(([w, c]) => `${w} (${genderNb(c)})`).join(', ')}.`),
        ],
        defs: g.categories.map((c) => [genderB(c), B(`Gender category: ${c}`, `Kjønnskategori: ${genderNb(c)}`)] as [Bi, Bi]),
        intuition: [B('Colour-code nouns by gender in your notes.', 'Fargekod substantivene etter kjønn i notatene dine.'), 'none'],
        mis: [[`${p}.mis.gender-meaning`, B('Guessing gender from meaning (e.g. that a "girl" word must be feminine)', 'Å gjette kjønn ut fra betydningen (f.eks. at et ord for «jente» må være hunkjønn)'), B('Grammatical gender does not always follow biology.', 'Grammatisk kjønn følger ikke alltid biologien.'), B('Learn the article with the noun.', 'Lær artikkelen sammen med substantivet.')]],
        apply: B('Use the correct article when you describe things.', 'Bruk riktig artikkel når du beskriver ting.'),
        keys: [[B('learn noun with article', 'lær substantivet med artikkelen'), ['article', 'gender', 'with', 'artikkel', 'kjønn', 'sammen']]],
      },
    });
  }

  if (spec.extraGrammar) {
    const eg = spec.extraGrammar;
    families.push(bankFamily({ skillId: eg.id, subjectId: spec.subjectId, objective: eg.name, items: eg.items, speechLang: spec.speechLang }));
    grammarDefs.push({ id: eg.id, name: eg.name, desc: eg.desc, stage: eg.stage, track: eg.track, pre: [`${p}.family`], range: [2, 6], target: 4, c: eg.content });
  }

  const orderId = `${p}.word-order`;
  families.push(wordOrderFamily(orderId, spec));
  grammarDefs.push({
    id: orderId, name: B('Sentence building & word order', 'Setningsbygging og ordstilling'), stage: 'G10', track: 'B1', range: [2, 7], target: 5, pre: [formsId],
    desc: spec.grammarNotes.order, aliases: ['word order', 'ordstilling', 'sentence construction'],
    c: {
      intro: B(`Word order in ${spec.name}: ${enOf(spec.grammarNotes.order)}`, `Ordstilling på ${N}: ${nbOf(spec.grammarNotes.order)}`),
      explain: spec.sentences.slice(0, 3).map((s) => pair(`${s.target} — ${s.en}`, s.nb && `${s.target} – ${s.nb}`)),
      defs: [[B('Word order', 'Ordstilling'), B('The sequence of subject, verb and other parts in a sentence.', 'Rekkefølgen av subjekt, verb og andre ledd i en setning.')]],
      intuition: [B('Build the sentence around the verb.', 'Bygg setningen rundt verbet.'), 'word-order'],
      mis: [[`${p}.mis.norwegian-order`, B('Copying Norwegian or English word order directly', 'Å kopiere norsk eller engelsk ordstilling direkte'), B('Each language has its own rules.', 'Hvert språk har sine egne regler.'), B('Learn model sentences and vary them.', 'Lær modellsetninger og varier dem.')]],
      apply: B('Write sentences about your week.', 'Skriv setninger om uka di.'),
      keys: [[B('position of the verb', 'hvor verbet står'), ['verb', 'position', 'second', 'first', 'plass', 'andre', 'først']]],
    },
  });

  /* ---------------- communication ---------------- */
  const readId = `${p}.reading`;
  families.push(bankFamily({ skillId: readId, subjectId: spec.subjectId, objective: 'Read for meaning', items: spec.reading.items.map((it) => ({ ...it, stimulus: { type: 'passage', title: spec.reading.title, text: spec.reading.text, lang: spec.speechLang } })), speechLang: spec.speechLang, range: [2, 6] }));

  const pronId = `${p}.pronunciation`;
  families.push(pronunciationFamily(pronId, spec));

  const writeId = `${p}.writing`;
  const kw = spec.intro.keywords;
  const ex = spec.intro.exemplar;
  const crits = () => [crit('name', B('States your name', 'Sier hva du heter'), kw.name), crit('age', B('States your age', 'Sier hvor gammel du er'), kw.age), crit('place', B('Says where you live', 'Sier hvor du bor'), kw.place)];
  families.push(bankFamily({
    skillId: writeId, subjectId: spec.subjectId, objective: 'Write a short presentation', range: [3, 7],
    items: [{
      id: 'intro', d: 4, kind: 'open-response',
      prompt: B(`Write 3–5 sentences in ${spec.name} introducing yourself: your name, your age and where you live. (Studio checks that all three parts are there.)`,
        `Skriv 3–5 setninger på ${N} der du presenterer deg selv: hva du heter, hvor gammel du er og hvor du bor. (Studio sjekker at alle tre delene er med.)`),
      rubric: { criteria: crits(), min: 0.66, exemplar: ex },
      solution: [B(`Example: ${ex}`, `Eksempel: ${ex}`)],
    }, {
      id: 'intro-plus', d: 6, kind: 'open-response',
      prompt: B(`Write 5–7 sentences in ${spec.name}: introduce yourself (name, age, home) **and** describe one family member.`, `Skriv 5–7 setninger på ${N}: presenter deg selv (navn, alder, hjemsted) **og** beskriv ett familiemedlem.`),
      rubric: {
        criteria: [...crits(), crit('family', B('Mentions a family member', 'Nevner et familiemedlem'), lexBy(['Family']).map((e) => e.w.toLowerCase().replace(/^(der|die|das|le|la|l')\s?/, '')))],
        min: 0.75, exemplar: ex,
      },
      solution: [B(`Example: ${ex}`, `Eksempel: ${ex}`)],
    }],
  }));

  const translateId = `${p}.translate`;
  families.push(translateFamily(translateId, spec));

  const commDefs: SkillDef[] = [
    {
      id: pronId, name: B('Pronunciation & listening discrimination', 'Uttale og lytting'), stage: 'G8', track: 'A1', range: [1, 5], target: 4,
      desc: B('Hear the difference between similar words (uses your device\'s speech synthesis when available).', 'Høre forskjell på ord som ligner (bruker talesyntesen på enheten når den finnes).'),
      aliases: ['pronunciation', 'uttale', 'listening'],
      c: {
        intro: B('Before you can say a sound, you must be able to hear it.', 'Før du kan si en lyd, må du kunne høre den.'),
        explain: [
          B('Studio plays a word with your device\'s text-to-speech voice. Choose what you heard.', 'Studio spiller av et ord med opplesingsstemmen på enheten din. Velg det du hørte.'),
          B('If your device has no voice for this language, the item becomes a reading item and Studio tells you.', 'Hvis enheten ikke har en stemme for dette språket, blir oppgaven en leseoppgave, og Studio sier fra.'),
        ],
        defs: [[B('Minimal pair', 'Minimalpar'), B('Two words that differ in only one sound.', 'To ord som skiller seg i bare én lyd.')]],
        intuition: [B('Listen for the one sound that differs.', 'Lytt etter den ene lyden som er forskjellig.'), 'none'],
        mis: [[`${p}.mis.sound`, B('Ignoring sounds that do not exist in Norwegian', 'Å overse lyder som ikke finnes på norsk'), B('Unfamiliar sounds are mapped to the nearest familiar one.', 'Ukjente lyder blir oppfattet som den nærmeste kjente lyden.'), B('Exaggerate the difference when practising.', 'Overdriv forskjellen når du øver.')]],
        apply: B('Repeat each word aloud after listening.', 'Gjenta hvert ord høyt etter at du har lyttet.'),
        keys: [[B('one sound changes the word', 'én lyd endrer ordet'), ['sound', 'differ', 'lyd', 'forskjell']]],
      },
    },
    {
      id: readId, name: B('Reading comprehension', 'Leseforståelse'), stage: 'G10', track: 'A2', range: [2, 6], target: 4, pre: [`${p}.family`, formsId],
      desc: B('Understand short texts about everyday life.', 'Forstå korte tekster om hverdagslivet.'), aliases: ['reading', 'lesing', 'leseforståelse'],
      c: {
        intro: B('Read for the main message first, then for details.', 'Les først for å finne hovedbudskapet, og deretter for detaljene.'),
        explain: [B('Skim the text once without stopping.', 'Skumles teksten én gang uten å stoppe.'), B('Then read the question and scan for the key word.', 'Les så spørsmålet og let etter nøkkelordet.')],
        defs: [[B('Skimming', 'Skumlesing'), B('Reading quickly for the overall idea.', 'Å lese raskt for å få med seg helheten.')], [B('Scanning', 'Letelesing'), B('Searching for a specific detail.', 'Å lete etter en bestemt detalj.')]],
        intuition: [B('Questions usually follow the order of the text.', 'Spørsmålene følger som regel rekkefølgen i teksten.'), 'none'],
        mis: [[`${p}.mis.every-word`, B('Stopping at every unknown word', 'Å stoppe ved hvert ukjente ord'), B('You lose the overall meaning.', 'Du mister helheten.'), B('Guess from context and continue.', 'Gjett ut fra sammenhengen og les videre.')]],
        apply: B('Read short messages and signs.', 'Les korte meldinger og skilt.'),
        keys: [[B('skim then scan', 'skumles først, let så'), ['skim', 'scan', 'skumles', 'let']]],
      },
    },
    {
      id: writeId, name: B('Writing short texts', 'Skrive korte tekster'), stage: 'G10', track: 'B1', range: [3, 7], target: 5, pre: [formsId, `${p}.family`],
      desc: B('Write a short self-presentation and describe people.', 'Skrive en kort presentasjon av deg selv og beskrive personer.'), aliases: ['writing', 'skriving'],
      c: {
        intro: B('Short, correct sentences beat long, broken ones.', 'Korte, riktige setninger er bedre enn lange setninger med feil.'),
        explain: [B('Include who you are, how old you are and where you live.', 'Ta med hvem du er, hvor gammel du er og hvor du bor.'), B('Re-use model sentences and change one element at a time.', 'Bruk modellsetninger om igjen, og bytt ut én del om gangen.')],
        defs: [[B('Model sentence', 'Modellsetning'), B('A correct sentence you adapt.', 'En riktig setning som du tilpasser.')]],
        intuition: [B('Build with blocks: subject + verb + information.', 'Bygg med klosser: subjekt + verb + informasjon.'), 'none'],
        mis: [[`${p}.mis.translate-word-for-word`, B('Translating word for word from Norwegian', 'Å oversette ord for ord fra norsk'), B('Word order and idioms differ.', 'Ordstilling og faste uttrykk er forskjellige.'), B('Start from model sentences.', 'Ta utgangspunkt i modellsetninger.')]],
        apply: B('Write a message to a pen-pal.', 'Skriv en melding til en brevvenn.'),
        keys: [[B('name, age, place', 'navn, alder, sted'), ['name', 'age', 'live', 'navn', 'alder', 'bor']]],
      },
    },
    {
      id: translateId, name: B('Translation', 'Oversettelse'), stage: 'G10', track: 'B1', range: [2, 7], target: 5, pre: [orderId],
      desc: B('Translate short sentences between English and the target language.', 'Oversette korte setninger mellom norsk og målspråket.'), aliases: ['translate', 'oversett'],
      c: {
        intro: B('Translate meaning, not single words.', 'Oversett meningen, ikke enkeltord.'),
        explain: [B('Identify subject, verb and object first.', 'Finn subjekt, verb og objekt først.'), B('Check verb form and word order in the target language.', 'Sjekk verbform og ordstilling i målspråket.')],
        defs: [[B('Idiom', 'Idiom'), B('A phrase whose meaning is not the sum of its words.', 'Et uttrykk som betyr noe annet enn summen av ordene.')]],
        intuition: [B('Meaning first, then form.', 'Mening først, så form.'), 'none'],
        mis: [[`${p}.mis.literal`, B('Literal word-for-word translation', 'Bokstavelig oversettelse ord for ord'), B('Languages structure sentences differently.', 'Språk bygger opp setninger på ulike måter.'), B('Re-read the result aloud.', 'Les resultatet høyt for deg selv.')]],
        apply: B('Translate a short message.', 'Oversett en kort melding.'),
        keys: [[B('meaning before words', 'mening før ord'), ['meaning', 'sense', 'mening', 'betydning']]],
      },
    },
  ];

  const domains: DomainDef[] = [
    { id: `${p}.d.vocab`, name: B('Vocabulary', 'Ordforråd'), desc: B(`Core ${spec.name} vocabulary.`, `Grunnleggende ordforråd på ${N}.`), topics: [{ name: B('Everyday words', 'Hverdagsord'), skills: vocabDefs }] },
    { id: `${p}.d.grammar`, name: B('Grammar', 'Grammatikk'), desc: B(`${spec.name} grammar foundations.`, `Grunnleggende grammatikk på ${N}.`), topics: [{ name: B('Forms & structure', 'Former og struktur'), skills: grammarDefs }] },
    { id: `${p}.d.comm`, name: B('Communication', 'Kommunikasjon'), desc: B('Listening, reading, writing and translation.', 'Lytting, lesing, skriving og oversettelse.'), topics: [{ name: B('Skills in use', 'Ferdigheter i bruk'), skills: commDefs }] },
  ];
  return { domains, families };
}

/* ------------------------------------------------------------------ */

function produceFamily(skillId: string, spec: LanguageSpec, entries: LexEntry[]): QuestionFamily {
  return {
    id: `${skillId}#produce`, skillId, objective: 'Produce the word', difficulties: [3, 6], kinds: ['translate', 'short-answer'],
    generate: (ctx) => {
      if (!entries.length) return null;
      const Q = forSkill(skillId, spec.subjectId, `${skillId}#produce`);
      const e = ctx.rng.pick(entries);
      const accepted = [e.w, ...spec.lexicon.filter((x) => x !== e && x.sense && x.sense === e.sense && x.en === e.en).map((x) => x.w)];
      return Q(ctx, {
        form: 'produce', params: e.w, kind: 'translate', difficulty: Math.max(3, ctx.difficulty) as Difficulty,
        ...text(accepted, tr(`in ${spec.name}`, `på ${spec.nameNb}`)),
        prompt: tr(`Write in ${spec.name}: **${e.en}**`, `Skriv på ${spec.nameNb}: **${mean(e)}**`),
        hints: [tr(`It starts with "${e.w.slice(0, 1)}".`, `Det begynner med «${e.w.slice(0, 1)}».`)],
        solution: [`${mean(e)} → ${e.w}`],
        speechLang: spec.speechLang,
      });
    },
  };
}

function listenFamily(skillId: string, spec: LanguageSpec, entries: LexEntry[]): QuestionFamily {
  return {
    id: `${skillId}#listen`, skillId, objective: 'Understand spoken words', difficulties: [2, 5], kinds: ['listening'],
    generate: (ctx) => {
      if (entries.length < 4) return null;
      const Q = forSkill(skillId, spec.subjectId, `${skillId}#listen`);
      const e = ctx.rng.pick(entries);
      const wrong = ctx.rng.shuffle(entries.filter((x) => x !== e && x.en !== e.en && (!e.sense || x.sense !== e.sense))).slice(0, 3).map(mean);
      return Q(ctx, {
        form: 'listen-meaning', params: e.w, kind: 'listening', difficulty: Math.min(5, Math.max(2, ctx.difficulty)) as Difficulty,
        ...choice(ctx.rng, mean(e), wrong),
        stimulus: { type: 'audio', text: e.w, lang: spec.speechLang },
        prompt: tr('Listen. What does the word or phrase mean?', 'Lytt. Hva betyr ordet eller uttrykket?'),
        hints: [tr('Play it again and focus on the first sound.', 'Spill det av igjen og lytt etter den første lyden.')],
        solution: [tr(`You heard "${e.w}" — ${stop(e.en)}`, `Du hørte «${e.w}» – ${stop(mean(e))}`)],
        speechLang: spec.speechLang,
      });
    },
  };
}

function formsFamily(skillId: string, spec: LanguageSpec): QuestionFamily {
  const keyName = (k: string) => tr(k, spec.formKeyNb?.[k] ?? k);
  const label = () => tr(spec.formKeyLabel, KEY_LABEL_NB[spec.formKeyLabel] ?? spec.formKeyLabel);
  const meaning = (t: FormTable) => tr(t.meaning, t.meaningNb ?? t.meaning);
  return {
    id: `${skillId}#forms`, skillId, objective: 'Use correct verb forms', difficulties: [1, 7], kinds: ['multiple-choice', 'fill-in', 'matching', 'translate'],
    generate: (ctx: GenContext): Question | null => {
      const { rng, difficulty: d } = ctx;
      const Q = forSkill(skillId, spec.subjectId, `${skillId}#forms`);
      const regular = spec.tables.filter((t) => !t.irregular);
      const table = d <= 2 ? rng.pick(spec.tables.slice(0, 2)) : d >= 5 ? rng.pick(spec.tables.filter((t) => t.irregular).concat(regular)) : rng.pick(spec.tables);
      const key = rng.pick(spec.formKeys);
      const form = table.forms[key];
      const others = [...new Set(spec.formKeys.map((k) => table.forms[k]).filter((f) => f !== form))];
      if (d <= 2) {
        return Q(ctx, {
          form: 'choose-form', params: `${table.verb}:${key}`, kind: 'multiple-choice', difficulty: d,
          ...choice(rng, spec.phrase(key, form), others.slice(0, 3).map((f) => [spec.phrase(key, f), `${spec.prefix}.mis.form`] as [string, string])),
          prompt: tr(`${table.verb} (${table.meaning}) — choose the correct form for **${key}**.`, `${table.verb} (${meaning(table)}) – velg riktig form for **${keyName(key)}**.`),
          hints: [`${label()}: ${keyName(key)}.`], solution: [`${spec.phrase(key, form)}.`], speechLang: spec.speechLang,
        });
      }
      if (d === 3 || d === 4) {
        const accepted = [form, spec.phrase(key, form)];
        const pattern = spec.formKeys.map((k) => spec.phrase(k, table.forms[k])).join(', ');
        return Q(ctx, {
          form: 'fill-form', params: `${table.verb}:${key}`, kind: 'fill-in', difficulty: d,
          ...text(accepted, tr('verb form', 'verbform')),
          prompt: tr(`Write the correct form of **${table.verb}** (${table.meaning}) for **${key}**.`, `Skriv riktig form av **${table.verb}** (${meaning(table)}) for **${keyName(key)}**.`),
          hints: [table.irregular ? tr('This verb is irregular — recall it rather than building it.', 'Dette verbet er uregelrett – hent det fram fra hukommelsen i stedet for å bygge det.') : tr('Take the stem and add the right ending.', 'Ta stammen og legg til riktig endelse.')],
          solution: [`${spec.phrase(key, form)}`, tr(`Full pattern: ${pattern}.`, `Hele mønsteret: ${pattern}.`)],
          speechLang: spec.speechLang,
        });
      }
      if (d === 5) {
        const keys = rng.sample(spec.formKeys.filter((k, i, arr) => arr.findIndex((x) => table.forms[x] === table.forms[k]) === i), 4);
        return Q(ctx, {
          form: 'match-forms', params: `${table.verb}:${keys.join(',')}`, kind: 'matching', difficulty: d,
          ...matching(rng, keys.map((k) => [keyName(k), table.forms[k]])),
          prompt: tr(`Match each ${spec.formKeyLabel.toLowerCase()} with the correct form of **${table.verb}** (${table.meaning}).`, `Koble hvert ${label().toLowerCase()} med riktig form av **${table.verb}** (${meaning(table)}).`),
          hints: [tr('Start with the forms you are sure of.', 'Begynn med formene du er sikker på.')], solution: keys.map((k) => spec.phrase(k, table.forms[k])), speechLang: spec.speechLang,
        });
      }
      // d6–7: pick a sentence and ask for translation choice where verb form differs
      const t2 = rng.pick(spec.tables);
      const k2 = rng.pick(spec.formKeys);
      const right = spec.phrase(k2, t2.forms[k2]);
      const wrongs = spec.formKeys.filter((k) => t2.forms[k] !== t2.forms[k2]).slice(0, 3).map((k) => spec.phrase(k2, t2.forms[k]));
      return Q(ctx, {
        form: 'form-in-context', params: `${t2.verb}:${k2}`, kind: 'error-correction', difficulty: d,
        ...choice(rng, right, wrongs.map((w) => [w, `${spec.prefix}.mis.form`] as [string, string])),
        prompt: tr(`Which is correct ${spec.name} for "${k2} + ${t2.meaning}" (${t2.verb})? Several options mix up the ${spec.formKeyLabel.toLowerCase()}.`, `Hva er riktig på ${spec.nameNb} for «${keyName(k2)} + ${meaning(t2)}» (${t2.verb})? Flere av alternativene har feil form.`),
        hints: [tr('Check the ending against the subject/aspect.', 'Sjekk endelsen mot subjektet/aspektet.')], solution: [right], speechLang: spec.speechLang,
      });
    },
  };
}

function wordOrderFamily(skillId: string, spec: LanguageSpec): QuestionFamily {
  return {
    id: `${skillId}#order`, skillId, objective: 'Build sentences', difficulties: [2, 7], kinds: ['sentence-construction', 'translate'],
    generate: (ctx) => {
      const { rng, difficulty: d } = ctx;
      const Q = forSkill(skillId, spec.subjectId, `${skillId}#order`);
      const s = rng.pick(spec.sentences);
      const order = bi(spec.grammarNotes.order);
      if (d <= 4) {
        return Q(ctx, {
          form: 'tiles', params: s.target, kind: 'sentence-construction', difficulty: d,
          ...tokens(rng, [s.target, ...(s.alt ?? [])]),
          prompt: tr(`Build the ${spec.name} sentence: "${s.en}"`, `Bygg setningen på ${spec.nameNb}: «${sentenceMeaning(s)}»`),
          hints: [order], solution: [s.target, ...(s.alt ?? []).map((a) => tr(`Also correct: ${a}`, `Også riktig: ${a}`))], speechLang: spec.speechLang,
        });
      }
      // harder: distractor tile + reasoning
      const extra = rng.pick(spec.lexicon.filter((e) => !s.target.includes(e.w))).w.split(' ')[0];
      return Q(ctx, {
        form: 'tiles-distractor', params: `${s.target}+${extra}`, kind: 'sentence-construction', difficulty: d,
        ...tokens(rng, [s.target, ...(s.alt ?? [])], [extra]),
        prompt: tr(`Build the ${spec.name} sentence for "${s.en}". One tile does not belong.`, `Bygg setningen på ${spec.nameNb} for «${sentenceMeaning(s)}». Én av brikkene hører ikke med.`),
        hints: [order], solution: [s.target], speechLang: spec.speechLang,
      });
    },
  };
}

function translateFamily(skillId: string, spec: LanguageSpec): QuestionFamily {
  return {
    id: `${skillId}#translate`, skillId, objective: 'Translate sentences', difficulties: [2, 7], kinds: ['translate', 'multiple-choice'],
    generate: (ctx) => {
      const { rng, difficulty: d } = ctx;
      const Q = forSkill(skillId, spec.subjectId, `${skillId}#translate`);
      const s = rng.pick(spec.sentences);
      const others = rng.shuffle(spec.sentences.filter((x) => x !== s)).slice(0, 3);
      if (d <= 3) {
        return Q(ctx, {
          form: 'to-english', params: s.target, kind: 'translate', difficulty: d,
          ...choice(rng, sentenceMeaning(s), others.map(sentenceMeaning)),
          prompt: tr(`What does this mean? **${s.target}**`, `Hva betyr dette? **${s.target}**`), hints: [tr('Find the verb first.', 'Finn verbet først.')], solution: [`${s.target} = ${sentenceMeaning(s)}`], speechLang: spec.speechLang,
        });
      }
      if (d <= 5) {
        return Q(ctx, {
          form: 'to-target', params: s.target, kind: 'translate', difficulty: d,
          ...choice(rng, s.target, others.map((o) => o.target)),
          prompt: tr(`Which ${spec.name} sentence means: "${s.en}"?`, `Hvilken setning på ${spec.nameNb} betyr «${sentenceMeaning(s)}»?`), hints: [tr('Check subject and verb.', 'Sjekk subjekt og verb.')], solution: [s.target], speechLang: spec.speechLang,
        });
      }
      return Q(ctx, {
        form: 'write-target', params: s.target, kind: 'translate', difficulty: d,
        ...text([s.target, ...(s.alt ?? [])], tr(`in ${spec.name}`, `på ${spec.nameNb}`)),
        prompt: tr(`Translate into ${spec.name}: "${s.en}"`, `Oversett til ${spec.nameNb}: «${sentenceMeaning(s)}»`), hints: [bi(spec.grammarNotes.order)], solution: [s.target], speechLang: spec.speechLang,
      });
    },
  };
}

function pronunciationFamily(skillId: string, spec: LanguageSpec): QuestionFamily {
  type Pair = LanguageSpec['minimalPairs'][number];
  const gloss = (mp: Pair, a: boolean) => (a ? tr(mp.aEn, mp.aNb ?? mp.aEn) : tr(mp.bEn, mp.bNb ?? mp.bEn));
  return {
    id: `${skillId}#pairs`, skillId, objective: 'Discriminate sounds', difficulties: [1, 5], kinds: ['listening', 'multiple-choice'],
    generate: (ctx) => {
      const { rng, difficulty: d } = ctx;
      const Q = forSkill(skillId, spec.subjectId, `${skillId}#pairs`);
      const mp = rng.pick(spec.minimalPairs);
      const playA = rng.chance(0.5);
      const heard = playA ? mp.a : mp.b;
      const other = playA ? mp.b : mp.a;
      if (d <= 3) {
        return Q(ctx, {
          form: 'which-word', params: `${mp.a}|${mp.b}|${heard}`, kind: 'listening', difficulty: d,
          ...choice(rng, heard, [other]),
          stimulus: { type: 'audio', text: heard, lang: spec.speechLang },
          prompt: tr('Listen. Which word did you hear?', 'Lytt. Hvilket ord hørte du?'), hints: [tr('Listen for the vowel or consonant that differs.', 'Lytt etter vokalen eller konsonanten som er forskjellig.')],
          solution: [tr(`You heard "${heard}" (${gloss(mp, playA)}). The other word is "${other}" (${gloss(mp, !playA)}).`, `Du hørte «${heard}» (${gloss(mp, playA)}). Det andre ordet er «${other}» (${gloss(mp, !playA)}).`)],
          speechLang: spec.speechLang,
        });
      }
      return Q(ctx, {
        form: 'heard-meaning', params: `${mp.a}|${mp.b}|${heard}`, kind: 'listening', difficulty: d,
        ...choice(rng, gloss(mp, playA), [gloss(mp, !playA), ...rng.sample(spec.minimalPairs.filter((x) => x !== mp).map((x) => gloss(x, true)), 2)]),
        stimulus: { type: 'audio', text: heard, lang: spec.speechLang },
        prompt: tr('Listen. What does the word you heard mean?', 'Lytt. Hva betyr ordet du hørte?'), hints: [tr('Two options sound almost the same.', 'To av alternativene høres nesten like ut.')],
        solution: [tr(`"${heard}" means ${gloss(mp, playA)}.`, `«${heard}» betyr ${gloss(mp, playA)}.`)], speechLang: spec.speechLang,
      });
    },
  };
}
